import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { retry } from 'src/utils';

import { postSnapchatEvent } from '@features/Analytics/api/snapchat';
import type {
  SnapchatEventName,
  TpSnapchatParameters,
} from '@features/Analytics/types/snapchat';
import { createEventId } from '@features/Analytics/utils/create-event-id';

const getStringValue = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : undefined;
  }

  if (typeof value === 'number') {
    return String(value);
  }
};

const getPurchaseId = (
  eventName: SnapchatEventName,
  options?: TpSnapchatParameters['options'],
) => {
  if (eventName !== 'PURCHASE') return;

  return (
    getStringValue(options?.order_id) ??
    getStringValue(options?.subscription_id) ??
    getStringValue(options?.bt_subscription_id) ??
    getStringValue(options?.transaction_id)
  );
};

const getEventOptions = ({
  eventName,
  eventId,
  options,
}: TpSnapchatParameters & { eventId: string }) => {
  const serverOptions = { ...options };
  const clientOptions = { ...options };
  const purchaseId = getPurchaseId(eventName, options);

  if (purchaseId) {
    clientOptions.transaction_id = purchaseId;

    serverOptions.custom_data = {
      ...(typeof serverOptions.custom_data === 'object' &&
      serverOptions.custom_data != null
        ? serverOptions.custom_data
        : {}),
      order_id: purchaseId,
    };
    delete serverOptions.transaction_id;
    delete serverOptions.order_id;
  }

  return {
    serverOptions,
    clientOptions: {
      ...clientOptions,
      client_dedup_id: eventId,
    },
  };
};

export const useSnapchatEvent = () => {
  const { mutateAsync: postEvent } = useMutation({
    mutationFn: postSnapchatEvent,
    mutationKey: ['post-snapchat-event'],
  });

  const conditionCheckFunction = useCallback(
    async () =>
      // @ts-ignore
      window?.snaptr != null,
    [],
  );

  const fetchClientEventWithRetry = useCallback(
    async ({ eventName, options }: TpSnapchatParameters) => {
      const eventId = createEventId(eventName);
      const { serverOptions, clientOptions } = getEventOptions({
        eventName,
        eventId,
        options,
      });

      const sclid = sessionStorage.getItem('ScCid');

      void postEvent({
        eventName,
        eventId,
        sc_click_id: sclid || undefined,
        options: serverOptions,
      }).catch((error: unknown) => {
        Sentry.captureException(error, {
          tags: { analytics_provider: 'snapchat', analytics_channel: 'server' },
          extra: { eventName, eventId },
        });
      });

      const actionFunction = async () => {
        // @ts-ignore
        window?.snaptr?.('track', eventName, clientOptions);
      };

      await retry({
        conditionCheckFunction,
        actionFunction,
        retryDelay: 300,
        maxRetries: 5,
      });
    },
    [conditionCheckFunction, postEvent],
  );

  return useMemo(
    () => ({
      clientEvent: ({ eventName, options }: TpSnapchatParameters) => {
        void fetchClientEventWithRetry({ eventName, options }).catch(
          (error: unknown) => {
            Sentry.captureException(error, {
              tags: {
                analytics_provider: 'snapchat',
                analytics_channel: 'client',
              },
              extra: { eventName },
            });
          },
        );
      },
    }),
    [fetchClientEventWithRetry],
  );
};
